// Stations, devices, held and rejected station actions: API calls and TanStack Query
// options. Every response is parsed with the shared @enp/domain/api schema (lib/api.ts).

import {
  type CreateStationRequest,
  type ResolveHeldCommandRequest,
  type UpdateStationRequest,
  deviceSummarySchema,
  heldCommandDetailSchema,
  heldCommandsResponseSchema,
  holdPreviewSchema,
  minAppVersionsResponseSchema,
  pairingCancelResponseSchema,
  pairingCodeResponseSchema,
  rejectedCommandDetailSchema,
  rejectedCommandsResponseSchema,
  stationSchema,
  stationsResponseSchema,
  type ReceiptCorrections,
} from '@enp/domain/api';
import type { MinAppVersions } from '@enp/domain/devices';
import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '../lib/api.js';

export const stationKeys = {
  all: ['stations'] as const,
  list: () => ['stations', 'list'] as const,
  holds: (status: HoldsFilter) => ['stations', 'holds', status] as const,
  hold: (id: string) => ['stations', 'hold', id] as const,
  rejected: () => ['stations', 'rejected'] as const,
  rejectedOne: (commandId: string) => ['stations', 'rejected', commandId] as const,
};

export const stationsQuery = () =>
  queryOptions({
    queryKey: stationKeys.list(),
    queryFn: ({ signal }) => api('/stations', { signal, schema: stationsResponseSchema }),
    retry: false,
  });

/**
 * Adds a station. `idempotencyKey`: one per opened form, so a resend after a lost answer
 * returns the station the first request made, never a second one (CR8B-10).
 */
export const createStation = (body: CreateStationRequest, idempotencyKey: string) =>
  api('/stations', {
    method: 'POST',
    body,
    schema: stationSchema,
    headers: { 'Idempotency-Key': idempotencyKey },
  });

export const updateStation = (id: string, body: UpdateStationRequest) =>
  api(`/stations/${encodeURIComponent(id)}`, { method: 'PATCH', body, schema: stationSchema });

export const createPairingCode = (stationId: string) =>
  api('/devices/pairing-codes', {
    method: 'POST',
    body: { station_id: stationId },
    schema: pairingCodeResponseSchema,
  });

export const revokeDevice = (id: string, reason: string) =>
  api(`/devices/${encodeURIComponent(id)}/revoke`, {
    method: 'POST',
    body: { reason },
    schema: deviceSummarySchema,
  });

export const updateMinVersions = (changes: Partial<MinAppVersions>) =>
  api('/devices/min-versions', {
    method: 'PUT',
    body: changes,
    schema: minAppVersionsResponseSchema,
  });

// ---------------------------------------------------------------- held station actions

/** The held-actions list's filter in the URL. */
export const holdsSearchSchema = z.object({
  status: z.enum(['open', 'resolved']).optional().catch(undefined),
});
export type HoldsFilter = 'open' | 'resolved';

export const holdsQuery = (status: HoldsFilter) =>
  queryOptions({
    queryKey: stationKeys.holds(status),
    queryFn: ({ signal }) =>
      api(`/held-commands?status=${status}`, { signal, schema: heldCommandsResponseSchema }),
    retry: false,
  });

export const holdQuery = (id: string) =>
  queryOptions({
    queryKey: stationKeys.hold(id),
    queryFn: ({ signal }) =>
      api(`/held-commands/${encodeURIComponent(id)}`, { signal, schema: heldCommandDetailSchema }),
    retry: false,
  });

export const resolveHold = (id: string, body: ResolveHeldCommandRequest) =>
  api(`/held-commands/${encodeURIComponent(id)}/resolve`, {
    method: 'POST',
    body,
    schema: heldCommandDetailSchema,
  });

/**
 * What applying a held action would do (run and rolled back on the server, UI3A-1); for a
 * held delivery, with the manager's corrections (D-066).
 */
export const holdPreviewQuery = (id: string, corrections?: ReceiptCorrections) =>
  queryOptions({
    queryKey: ['stations', 'hold', id, 'preview', corrections ?? null] as const,
    queryFn: ({ signal }) =>
      corrections
        ? api(`/held-commands/${encodeURIComponent(id)}/preview`, {
            method: 'POST',
            body: { corrections },
            signal,
            schema: holdPreviewSchema,
          })
        : api(`/held-commands/${encodeURIComponent(id)}/preview`, {
            signal,
            schema: holdPreviewSchema,
          }),
    retry: false,
    // Always fresh: the ledger moves on between two openings of the dialog.
    staleTime: 0,
    gcTime: 0,
  });

/** Stops a pairing key at once (the dialog closed without a device, UI3A-4). */
export const cancelPairingCode = (pairingId: string) =>
  api(`/devices/pairing-codes/${encodeURIComponent(pairingId)}/cancel`, {
    method: 'POST',
    schema: pairingCancelResponseSchema,
  });

// ------------------------------------------------------------ rejected station actions

export const rejectedQuery = () =>
  queryOptions({
    queryKey: stationKeys.rejected(),
    queryFn: ({ signal }) =>
      api('/rejected-commands', { signal, schema: rejectedCommandsResponseSchema }),
    retry: false,
  });

export const rejectedCommandQuery = (commandId: string) =>
  queryOptions({
    queryKey: stationKeys.rejectedOne(commandId),
    queryFn: ({ signal }) =>
      api(`/rejected-commands/${encodeURIComponent(commandId)}`, {
        signal,
        schema: rejectedCommandDetailSchema,
      }),
    retry: false,
  });

/** After any change: stations, holds and their counts are read again. */
export const invalidateStations = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: stationKeys.all });
